import { useMutation } from '@tanstack/react-query';
import { useRef } from 'react';

import { ActionData } from '@bonusly/common-action-input';
import { useDispatch } from '@bonusly/events';
import { useHttpClient } from '@bonusly/http-client';

import { BonusVia, GiveBoxEvent } from '../events';
import { createTrackingData } from '../utils/analytics';
import { createBonusData } from '../utils/create-bonus-data';
import { parseErrorMessage } from '../utils/error';
import { useBonusBoxAnalytics } from './use-bonus-box-analytics';

interface UseGiveRecognitionConfig {
	onError?: (error: unknown) => void;
	onSuccess?: VoidFunction;
	via?: BonusVia;
	msTeamsMeetingId?: string;
	wrapPost?: boolean;
	draftAssistInteractionIds?: string[];
	onBehalfOf?: string;
}

export const useGiveRecognition = ({
	onError,
	onSuccess,
	via,
	msTeamsMeetingId,
	wrapPost,
	draftAssistInteractionIds,
	onBehalfOf,
}: UseGiveRecognitionConfig = {}) => {
	const http = useHttpClient('v2');
	const analytics = useBonusBoxAnalytics();
	const dispatch = useDispatch();

	// React Query can hold a stale mutationFn closure across renders, so the
	// submit must read every caller-supplied value at call time — e.g. clearing
	// a Bizy hand-off mid-session must drop application_name before submit. Keep
	// one ref of the latest values so this workaround is stated once and does not
	// grow a new two-line mirror per field.
	const latestRef = useRef({ via, msTeamsMeetingId, wrapPost, draftAssistInteractionIds, onBehalfOf });
	latestRef.current = { via, msTeamsMeetingId, wrapPost, draftAssistInteractionIds, onBehalfOf };

	const { mutate, isLoading: isSubmitting } = useMutation<{ data: { result: any } }, unknown, ActionData, unknown>(
		(action) => {
			const {
				via: currentVia,
				msTeamsMeetingId: currentMsTeamsMeetingId,
				wrapPost: currentWrapPost,
				draftAssistInteractionIds: currentIds,
				onBehalfOf: onBehalfOfValue,
			} = latestRef.current;
			const data = {
				bonus: {
					...createBonusData({ action }),
					wrap_post: currentWrapPost,
					...(currentIds?.length ? { draft_assist_interaction_ids: currentIds } : {}),
					...(onBehalfOfValue ? { on_behalf_of: onBehalfOfValue } : {}),
				},
			};

			const params = {
				include_children: true,
			};

			if (currentVia) {
				Object.assign(params, { application_name: currentVia });
			}

			if (currentMsTeamsMeetingId) {
				Object.assign(params, { ms_teams_meeting_id: currentMsTeamsMeetingId });
			}

			return http.post('/bonuses', data, { params });
		},
		{
			onError: (err: unknown) => {
				analytics.trackSubmitFailure({
					reason: parseErrorMessage(err),
				});

				onError?.(err);
			},
			onSuccess: (data, vars) => {
				const bonus = data.data.result;
				const totalAmount = vars.totalAmount;

				dispatch(GiveBoxEvent.Created, { bonus, totalAmount });
				analytics.trackSubmitSuccess(createTrackingData(vars));
				onSuccess?.();
			},
		}
	);

	return {
		isSubmitting,
		onSubmit: mutate,
	};
};
